<template>
	<v-layout
		align-center
		justify-center
		column
		tabindex="0"
		@keydown="handleKeyDown"
	>
		<v-card flat style="background-color: var(--v-light-primary-base);">
			<v-card-text>
				<v-text-field
					v-if="isOnCall"
					:value="phoneNumber"
					clearable
					flat
					solo
					background-color="light-primary"
					class="centered-input text-h6 font-weight-semibold"
					hide-details="auto"
					@click:clear="phoneNumber=''"
				/>
				<v-col v-else class="px-0 pb-0">
					<combobox-phone
						ref="phone"
						v-model="phoneNumber"
						background-color="light-primary"
						:placeholder="$t('add-phone')"
						:label="$t('phone')"
						:default-country="defaultCountry"
						:is-dialpad="true"
						@validate="onValidate"
						@update-search="setPhoneNumber"
					/>
				</v-col>
				<div>
					<v-layout
						v-for="(row, rowIndex) in buttons"
						:key="rowIndex"
						no-gutters
						align-center
						justify-center
					>
						<v-btn
							v-for="(button, buttonIndex) in row"
							:key="buttonIndex"
							color="primary"
							fab
							outlined
							class="ma-1"
							@click="addDigit(button)"
						>
							<span class="text-h5 font-weight-bold">
								{{ button }}
							</span>
						</v-btn>
					</v-layout>
					
					<v-layout no-gutters align-center justify-center>
						<template v-if="!isOnCall">
							<v-btn
								color="primary"
								fab
								depressed
								class="ma-2 "
								:disabled="!isValid"
								@click="outboundCall"
							>
								<v-icon large>mdi-phone-in-talk</v-icon>
							</v-btn>
						</template>
					</v-layout>

					<template v-if="!isOnCall && extensions.length">
						<v-divider class="my-2" />
						<div class="text-caption text--secondary mb-1">
							{{ $t("transfer-extensions") }}
						</div>
						<v-row dense>
							<v-col v-for="extension in extensions" :key="extension.id" cols="6">
								<v-btn
									block
									outlined
									color="primary"
									class="extension-btn text-none justify-start"
									@click="extensionCall(extension)"
								>
									<v-icon left small>
										{{ extension.type === 'partner' ? 'mdi-domain' : 'mdi-phone-classic' }}
									</v-icon>
									<span class="text-truncate">{{ extension.name }}</span>
								</v-btn>
							</v-col>
						</v-row>
					</template>
				</div>
			</v-card-text>
		</v-card>
	</v-layout>
</template>

<script>
import { PhoneValidator } from "@/validators";
import { AVAILABLE_COUNTRIES } from "@/common/const";
import { mapGetters, mapActions } from "vuex";
import phone from "phone";
import ComboboxPhone from "../common/ComboboxPhone.vue";
import { bus } from "@/main.js";

export default {
	name: "Dialpad",
	components: { ComboboxPhone },
	
	props: {
		isActiveCall: {
			type: Boolean,
			default: false
		}
	},
	
	data() {
		return {
			buttons: [
				["1", "2", "3"],
				["4", "5", "6"],
				["7", "8", "9"],
				["*", "0", "#"]
			],
			defaultCountry: "CA",
			isValid: false,
			availableCountries: AVAILABLE_COUNTRIES,
			phoneNumber: "",
			extensions: [],
			// Flag to prevent default when in text field
			focusedOnTextField: false,
			rules: {
				phoneValidator: PhoneValidator
			}
		};
	},
	
	computed: {
		...mapGetters("voice", ["isOnCall", "activeCall"]),
		...mapGetters("clinic", ["enableExtTransfer"])
	},

	watch: {
		// Tier permissions load asynchronously and may arrive after mount
		enableExtTransfer: {
			immediate: true,

			handler(enabled) {
				if (enabled) {
					this.loadExtensions();
				} else {
					this.extensions = [];
				}
			}
		}
	},
	
	methods: {
		...mapActions("voice", ["hangup"]),

		async loadExtensions() {
			if (this.isOnCall || !this.enableExtTransfer) return;

			try {
				const { data } = await this.axios.get("transfer-extension");

				this.extensions = (data ?? []).sort((a, b) => a.name.localeCompare(b.name));
			} catch (err) {
				console.error("Unable to load transfer extensions", err);
			}
		},

		extensionCall(extension) {
			bus.$emit("start-outbound-call", { extension });
			this.$emit("outboundCallStarted");
		},

		handleKeyDown(event) {
			if (event.key === "Enter") {
				if (this.isValid && !this.isOnCall) {
					this.outboundCall();
				}
			}
		},

		addDigit(digit) {
			if (this.activeCall?.callInstance) {
				this.activeCall.callInstance.sendDigits(digit);
			}
			
			this.phoneNumber += digit;
		},

		clearPhoneNumber() {
			this.phoneNumber = "";
		},

 		onValidate(isValidPhone) {
 			this.isValid = !!isValidPhone;
 		},
		
		async outboundCall() {
			if (this.phoneNumber) {
				bus.$emit("start-outbound-call", { phoneNumber: this.phoneNumber });
				this.$emit("outboundCallStarted");
			} else {
				throw new Error(this.$t("error.call-contact"));
			}
		},
		
		setPhoneNumber(value) {
			this.phoneNumber = value;
 		}
	}
};
</script>

<style scoped>
.extension-btn ::v-deep .v-btn__content {
	max-width: 100%;
}
</style>